import { CloseIcon } from "@xola/icons";
import { Button, Drawer } from "@xola/ui-kit";
import clsx from "clsx";
import React from "react";
import { CanvasLocation, CanvasPresentation } from "types";
import { canvasSizePx, normalizeCanvasSize } from "./constants";
import { SIDEBAR_OFFSET } from "@/utils/sidebar";

// ui-kit owns every width except 2xl, whose own class (w-screen md:max-w-screen-md
// 2xl:max-w-screen-lg) only reaches 768px below the 2xl breakpoint. Pin it to the same 1024 ceiling
// the push and left drawers use, clamped so it can never exceed the viewport.
const TWO_XL_PANEL_CLASS = "!w-[min(1024px,100vw)]";

interface EmbeddedAppDrawerProps {
    isOpen: boolean;
    canvasLocation?: CanvasLocation;
    canvasPresentation?: CanvasPresentation;
    canvasSize?: string;
    onClose: () => void;
    children: React.ReactNode;
}

export const EmbeddedAppDrawer = ({
    isOpen,
    canvasLocation = CanvasLocation.RIGHT,
    canvasPresentation = CanvasPresentation.OVERLAY,
    canvasSize,
    onClose,
    children,
}: EmbeddedAppDrawerProps) => {
    if (canvasLocation === CanvasLocation.LEFT && canvasPresentation === CanvasPresentation.PUSH) {
        return (
            <LeftPushDrawer isOpen={isOpen} canvasSize={canvasSize} onClose={onClose}>
                {children}
            </LeftPushDrawer>
        );
    }

    if (canvasLocation === CanvasLocation.LEFT) {
        return (
            <LeftSideDrawer isOpen={isOpen} canvasSize={canvasSize} onClose={onClose}>
                {children}
            </LeftSideDrawer>
        );
    }

    if (canvasPresentation === CanvasPresentation.PUSH) {
        return (
            <RightPushDrawer isOpen={isOpen} canvasSize={canvasSize} onClose={onClose}>
                {children}
            </RightPushDrawer>
        );
    }

    const size = normalizeCanvasSize(canvasSize);
    const widthOverride = size === "2xl" ? TWO_XL_PANEL_CLASS : "";

    return (
        <Drawer
            isOpen={isOpen}
            size={size}
            content={children}
            classNames={{ dialog: "z-20", children: clsx("!p-0 h-full", widthOverride), content: "mt-0 h-full" }}
            onClose={onClose}
        />
    );
};

interface LeftSideDrawerProps {
    isOpen: boolean;
    canvasSize?: string;
    onClose: () => void;
    children: React.ReactNode;
}

// Overlay left drawer. Clip wrapper (overflow-hidden) prevents the sliding panel
// from covering the sidebar during the transition.
const LeftSideDrawer = ({ isOpen, canvasSize, onClose, children }: LeftSideDrawerProps) => {
    // Shares canvasSizePx with the push drawers so the same canvasSize is the same width either way.
    const widthPx = canvasSizePx(canvasSize);

    return (
        <div
            className={clsx("fixed top-0 bottom-0 right-0 z-20 overflow-hidden", !isOpen && "pointer-events-none")}
            style={{ left: SIDEBAR_OFFSET }}
        >
            <div
                className={clsx(
                    "absolute inset-0 transition-opacity duration-500 sm:duration-700",
                    isOpen ? "bg-black/20" : "pointer-events-none opacity-0",
                )}
                onClick={onClose}
            />
            {/* Panel + close button slide as a unit so the button appears outside the panel edge */}
            <div
                className="absolute top-0 left-0 bottom-0 flex transition-transform duration-500 ease-in-out sm:duration-700"
                style={{ transform: isOpen ? "translateX(0)" : "translateX(-100%)" }}
            >
                <div className="h-full bg-white shadow-xl" style={{ width: `min(${widthPx}px, 100%)` }}>
                    {children}
                </div>
                <DrawerCloseButton onClick={onClose} />
            </div>
        </div>
    );
};

interface PushDrawerProps {
    isOpen: boolean;
    canvasSize?: string;
    onClose: () => void;
    children: React.ReactNode;
}

const LeftPushDrawer = ({ isOpen, canvasSize, onClose, children }: PushDrawerProps) => {
    const widthPx = canvasSizePx(canvasSize);

    return (
        // Clip wrapper: overflow-hidden ensures the sliding row never renders on top of the
        // sidebar even though the drawer has a higher z-index than the sidebar.
        <div
            className="pointer-events-none fixed top-0 bottom-0 right-0 z-20 overflow-hidden"
            style={{ left: SIDEBAR_OFFSET }}
        >
            <div
                className={clsx(
                    "absolute top-0 left-0 bottom-0 flex transition-transform duration-500 ease-in-out sm:duration-700",
                    isOpen ? "pointer-events-auto" : "pointer-events-none",
                )}
                style={{ transform: isOpen ? "translateX(0)" : "translateX(-100%)" }}
            >
                <div
                    className="h-full border-r border-gray-light bg-white"
                    style={{ width: `min(${widthPx}px, 100%)` }}
                >
                    {children}
                </div>
                <DrawerCloseButton onClick={onClose} />
            </div>
        </div>
    );
};

const RightPushDrawer = ({ isOpen, canvasSize, onClose, children }: PushDrawerProps) => {
    const widthPx = canvasSizePx(canvasSize);

    return (
        <div
            className={clsx("fixed top-0 right-0 bottom-0 z-20 flex", !isOpen && "pointer-events-none")}
            style={{
                transform: isOpen ? "translateX(0)" : "translateX(100%)",
                transition: "transform 500ms ease-in-out",
            }}
        >
            <DrawerCloseButton onClick={onClose} />
            <div className="h-full border-l border-gray-light bg-white" style={{ width: `min(${widthPx}px, 100vw)` }}>
                {children}
            </div>
        </div>
    );
};

// Matches the ui-kit Drawer's CloseButton: white circle, m-2.5, self-start so it
// doesn't stretch to the panel height.
export const DrawerCloseButton = ({ onClick }: { onClick: () => void }) => (
    <Button
        size="small"
        variant="link"
        className="m-2.5 inline-flex !h-10 !w-10 flex-shrink-0 items-center justify-center self-start !rounded-full bg-white !px-1.5 !text-black shadow-md"
        aria-label="Close"
        onClick={onClick}
    >
        <CloseIcon size="medium" />
    </Button>
);
